import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SmartMoveThemeMode } from '../models/smartmove-shell.models';

const SMARTMOVE_THEME_KEY = 'smartmove.themeMode';

@Injectable({ providedIn: 'root' })
export class SmartMoveThemeService {
  private readonly modeSubject: BehaviorSubject<SmartMoveThemeMode>;

  readonly mode$: Observable<SmartMoveThemeMode>;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.modeSubject = new BehaviorSubject<SmartMoveThemeMode>(
      this.readStoredMode()
    );
    this.mode$ = this.modeSubject.asObservable();
  }

  initialise(): void {
    this.applyMode(this.modeSubject.value);
  }

  toggle(): void {
    this.setMode(this.modeSubject.value === 'dark' ? 'light' : 'dark');
  }

  setMode(mode: SmartMoveThemeMode): void {
    this.writeStoredMode(mode);
    this.modeSubject.next(mode);
    this.applyMode(mode);
  }

  private readStoredMode(): SmartMoveThemeMode {
    try {
      return this.getStorage()?.getItem(SMARTMOVE_THEME_KEY) === 'dark'
        ? 'dark'
        : 'light';
    } catch {
      return 'light';
    }
  }

  private writeStoredMode(mode: SmartMoveThemeMode): void {
    try {
      this.getStorage()?.setItem(SMARTMOVE_THEME_KEY, mode);
    } catch {
      // Theme selection remains usable when storage is unavailable.
    }
  }

  private getStorage(): Storage | null {
    try {
      return this.document.defaultView?.localStorage || null;
    } catch {
      return null;
    }
  }

  private applyMode(mode: SmartMoveThemeMode): void {
    this.document.documentElement.dataset.smartmoveTheme = mode;
  }
}
